// Pure layout helpers for the booking groups Gantt. Dates are "YYYY-MM-DD"
// strings and every column is a NIGHT: a bar from arrival to departure covers
// arrival .. departure - 1.

const DAY_MS = 24 * 60 * 60 * 1000;

function toUtc(iso) {
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

/** Whole days from a to b (b - a). */
export function daysBetween(a, b) {
  return Math.round((toUtc(b) - toUtc(a)) / DAY_MS);
}

/** Campus-seasons with at least one night in the year, active ones only. */
export function seasonsInYear(campuses, year) {
  const jan1 = `${year}-01-01`;
  const dec31 = `${year}-12-31`;
  return (campuses || [])
    .filter((c) => c && c.active !== false && c.arrivalDate <= dec31 && c.departureDate > jan1)
    .sort((a, b) => (a.arrivalDate < b.arrivalDate ? -1 : a.arrivalDate > b.arrivalDate ? 1 : a.name.localeCompare(b.name)));
}

/** Chip label: the name, plus dates when the same name runs more than one season. */
export function seasonLabel(campus, all) {
  const twins = (all || []).filter((c) => c.name === campus.name).length;
  if (twins < 2) return campus.name;
  return `${campus.name} ${shortDate(campus.arrivalDate)}–${shortDate(campus.departureDate)}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export function shortDate(iso) {
  if (!iso) return "";
  const [, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return `${d} ${MONTHS[m - 1]}`;
}

export function weekday(iso) {
  return WEEKDAYS[new Date(toUtc(iso)).getUTCDay()];
}

export function dayOfMonth(iso) {
  return Number(String(iso).slice(8, 10));
}

/** Month header: [{ label: "Jun 2027", span }] over the day columns. */
export function monthSpans(dates) {
  const out = [];
  for (const iso of dates) {
    const [y, m] = iso.split("-").map(Number);
    const label = `${MONTHS[m - 1]} ${y}`;
    const last = out[out.length - 1];
    if (last && last.label === label) last.span += 1;
    else out.push({ label, span: 1 });
  }
  return out;
}

/**
 * A group row as table cells over the day columns:
 *   { kind: "empty", index } before and after the bar,
 *   { kind: "bar", index, span, clippedStart, clippedEnd } for the nights it covers.
 * Bars outside the axis give only empty cells.
 */
export function barCells(dates, arrival, departure) {
  const n = dates.length;
  const cells = [];
  if (!n) return cells;
  const start = daysBetween(dates[0], arrival);
  const endExclusive = daysBetween(dates[0], departure);
  const from = Math.max(0, start);
  const to = Math.min(n, endExclusive);
  if (!arrival || !departure || to <= from) {
    for (let i = 0; i < n; i++) cells.push({ kind: "empty", index: i });
    return cells;
  }
  for (let i = 0; i < from; i++) cells.push({ kind: "empty", index: i });
  cells.push({ kind: "bar", index: from, span: to - from, clippedStart: start < 0, clippedEnd: endExclusive > n });
  for (let i = to; i < n; i++) cells.push({ kind: "empty", index: i });
  return cells;
}

/** Bar label: "Group · Agent · 35 + 3". */
export function barLabel(bar) {
  const parts = [bar.name || `Group ${bar.groupId}`];
  if (bar.agentName && bar.agentName !== bar.name) parts.push(bar.agentName);
  parts.push(`${bar.students || 0} + ${bar.adults || 0}`);
  return parts.join(" · ");
}

/** Background for an independent-students cell: darker with more students on site. */
export function indieShade(count, peak) {
  if (!count) return "transparent";
  const t = peak > 0 ? count / peak : 0;
  const alpha = 0.15 + 0.65 * Math.min(1, t);
  return `rgba(103, 58, 183, ${alpha.toFixed(2)})`;
}

/** The totals rows under each centre, in order. */
export const TOTAL_ROWS = [
  { key: "students", label: "Students" },
  { key: "adults", label: "Adults" },
  { key: "teflStaff", label: "TEFL staff (÷16)" },
  { key: "activityStaff", label: "Activity staff (÷12)" },
  { key: "bedsNeeded", label: "Total beds needed", strong: true },
  { key: "bedsAvailable", label: "Beds available" },
  { key: "bedsRemaining", label: "Beds remaining", strong: true, warnBelowZero: true },
];

export function formatNumber(n, digits = 0) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString("en-GB", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
